<template lang="pug">
transition(enter-active-class="duration-200 ease-out" leave-active-class="duration-200 ease-in")
  EcreativMoleculesLocationBaseCard.callbackCard(v-if="show" :expandable="false")
    template(#header)
      button.callbackCard__close.position-absolute.text-secondary(
        type="button"
        @click="$emit('close')"
        aria-label="Close"
      )
        svg(width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="bi bi-x-lg")
          path(
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          )
    DynamicForm.mt-4(
      :send-email="false"
      :save-firebase="true"
      :schema="formSchema"
      :firebaseConfig="firebaseConfig"
      firebase-collection="callbackRequests"
      :form-name="'Call Back'"
      form-ref="CallbackForm"
      :big-form="false"
      :isLoading="loading"
      secondary
      @formBuilderOnSubmit="onFormSubmit"
    )
      template(#thankyou)
        .text-center
          svg(class="bi bi-check-circle-fill text-success" width="64" height="64" fill="currentColor" viewBox="0 0 16 16")
            path(d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.071-.02l3.992-4.99a.75.75 0 1 0-1.139-.976L7.477 9.417 5.384 7.323a.75.75 0 1 0-1.06 1.061l2.647 2.647z")
          h5.modal-title.mt-3.mb-2 Call Back Request Submitted
          p.mb-4 Thank you! We've passed your request to {{ imamName }}. You'll receive a call as soon as possible using the number you provided.
          button.btn.btn-primary(type="button" @click="$emit('close')") Close
      template(#headingSection)
        .justify-content-between.align-items-start.me-2
          EcreativArchetypesHeading.callbackCard__title.mb-2(
            tag="h3"
            size="5"
          ) {{ heading }}
          EcreativArchePrimitivesParagraph.m-0(size="6" context="info") {{ subTitle }}
          EcreativArchePrimitivesParagraph.mt-2(
            size="7"
            context="dark"
          ) {{ description }}
      template(#loader)
        EcreativAtomsSpinner
      template(#submitText)
        span.text-white.text-uppercase.fw-bold Submit Request
      template(#privacyPolicy)
        p.m-0.mb-2
          b
            | I have read and agree to the&nbsp;
            nuxt-link(target="_blank" to="https://docs.trueislam.co.uk/ama-uk-privacy-notice.pdf") Privacy Policy
      template(#hiddenInputs)
        input(type="hidden" name="userTitle"    :value="contactTitle")
        input(type="hidden" name="userName"     :value="contactName")
        input(type="hidden" name="userEmail"    :value="contactEmail")
        input(type="hidden" name="userPostcode" :value="contactPostcode")
        input(type="hidden" name="imamId"       :value="String(imamId)")
        input(type="hidden" name="imamName"     :value="imamName")
        input(type="hidden" name="imamSubtitle" :value="subTitle")
        input(type="hidden" name="requestType"  value="callback")
        input(type="hidden" name="distance"     :value="String(distance)")
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useNuxtApp } from '#app';
import * as Yup from 'yup';

const props = defineProps({
  show: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  heading: { type: String, required: true },
  subTitle: { type: String, default: '' },
  description: { type: String, default: '' },
  placeholder: { type: String, default: 'Your Phone Number' },
  modelValue: { type: String, default: '' },
  contactTitle: { type: String, default: '' },
  contactName: { type: String, default: '' },
  contactEmail: { type: String, default: '' },
  contactPostcode: { type: String, default: '' },
  imamId: { type: [String, Number], default: '' },
  imamName: { type: [String, Number], default: '' },
  distance: { type: [String, Number], default: '' },
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'update:modelValue', v: string): void;
}>();

const { $firestoreConf } = useNuxtApp();
const firebaseConfig = $firestoreConf as Record<string, string>;

const phoneInitial = ref(props.modelValue);
watch(
  () => props.modelValue,
  (v) => (phoneInitial.value = v),
);

const formSchema = computed(() => [
  {
    title: '',
    description: '',
    fields: [
      {
        placeholder: props.placeholder,
        name: 'inputMobile',
        as: 'input',
        type: 'tel',
        value: phoneInitial.value,
        rules: Yup.string()
          .required('Phone is required')
          .transform((v) => (v || '').replace(/\s|[-().]/g, ''))
          .matches(
            /^(?:0\d{10}|\+44\d{10})$/,
            'Enter a valid UK phone number (e.g. 07123 456 789 or +44 7123 456 789)',
          ),
      },
    ],
  },
]);

function onFormSubmit(payload: Record<string, unknown>) {
  const phone = String(payload.inputMobile ?? '').trim();
  emit('update:modelValue', phone);
}
</script>

<style lang="scss" scoped>
$block: '.callbackCard';

#{$block} {
  $block: &;

  &#{$block} {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 24px;
    background-color: #fff;
    border-radius: $border-radius-xl;
  }

  #{$block}__header {
    margin-bottom: 1rem;
  }
  #{$block}__title {
    margin-bottom: 8px;
  }

  #{$block}__inputWrapper {
    margin-top: 16px;

    input {
      padding: 16px;
      border: 2px solid $green-200;
      outline: none;
      font-size: 1rem;
      border-radius: $border-radius-lg;

      &:disabled {
        opacity: 0.6;
      }
    }
  }

  #{$block}__close {
    top: 24px;
    right: 24px;
    cursor: pointer;
  }
}
</style>
